// SEARCH ALL CALLS — full-history call-for-service search (open, pending,
// dispatched, completed, closed, archived). Reachable from Command Center & RMS.
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import client from "@/lib/api";
import Layout, { PageHeader } from "@/components/Layout";
import { PriorityBadge, DiscBadge } from "@/components/badges";
import { Search, Loader2, AlertTriangle, ChevronLeft, ChevronRight, ArrowUpDown, Eraser, FileDown } from "lucide-react";
import { downloadCallEventPdf } from "@/lib/callEventPdf";

const STATUSES = ["", "pending", "dispatched", "enroute", "active", "onscene", "delayed_onscene", "completed", "closed"];
const STATUS_OPT_LABELS = { enroute: "EN ROUTE", delayed_onscene: "DELAYED ON SCENE" };
const EMPTY = { q: "", cc: "", case: "", caller: "", phone: "", address: "", call_type: "", unit: "", status: "", priority: "", date_from: "", date_to: "" };

export default function SearchCalls() {
  const [f, setF] = useState(EMPTY);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState({ by: "created_at", order: "desc" });
  const nav = useNavigate();

  const run = useCallback(async (pg = 1, srt = sort) => {
    setBusy(true); setError("");
    try {
      const params = { page: pg, page_size: 25, sort_by: srt.by, order: srt.order };
      Object.entries(f).forEach(([k, v]) => { if (String(v).trim() !== "") params[k] = String(v).trim(); });
      const { data: d } = await client.get("/calls/search", { params });
      setData(d); setPage(d.page);
    } catch (e) {
      setError(e.response?.data?.detail || "Call search failed — the error has been logged for troubleshooting.");
      setData(null);
    } finally { setBusy(false); }
  }, [f, sort]);

  useEffect(() => { run(1); }, []); // eslint-disable-line

  const toggleSort = (by) => {
    const s = { by, order: sort.by === by && sort.order === "desc" ? "asc" : "desc" };
    setSort(s); run(page, s);
  };

  const inp = "bg-[#141518] border border-[#2C2D35] px-2 py-1.5 text-xs font-mono outline-none focus:border-[#3B82F6] text-white w-full";
  const lbl = "font-mono text-[9px] text-[#9CA3AF] uppercase";
  const TH = ({ id, children }) => (
    <th className="px-2 py-2 cursor-pointer select-none hover:text-white" onClick={() => toggleSort(id)}
      data-testid={`search-sort-${id}`}>
      <span className="flex items-center gap-1">{children}<ArrowUpDown size={9} className={sort.by === id ? "text-[#3B82F6]" : "text-[#4B5563]"} /></span>
    </th>
  );

  return (
    <Layout>
      <PageHeader title="Search All Calls" sub="Matcom Records Division · Full authorized call-for-service history — open, pending, dispatched, completed, closed & archived" seal="/brand/records-seal.png" />
      <div className="flex-1 overflow-hidden flex flex-col">
        {/* filters */}
        <form onSubmit={(e) => { e.preventDefault(); run(1); }} className="p-3 border-b border-[#2C2D35] bg-[#0d0e11] space-y-2">
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2">
            <div className="col-span-2"><label className={lbl}>Keyword — notes, narrative, caller, address, CAD/case #</label>
              <input data-testid="search-q" value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} placeholder="Any keyword…" className={inp} /></div>
            <div><label className={lbl}>CAD # (CC / public ref)</label>
              <input data-testid="search-cc" value={f.cc} onChange={(e) => setF({ ...f, cc: e.target.value })} placeholder="2026-12345" className={inp} /></div>
            <div><label className={lbl}>Case # (RMS)</label>
              <input data-testid="search-case" value={f.case} onChange={(e) => setF({ ...f, case: e.target.value })} placeholder="Case number" className={inp} /></div>
            <div><label className={lbl}>Caller name</label>
              <input data-testid="search-caller" value={f.caller} onChange={(e) => setF({ ...f, caller: e.target.value })} className={inp} /></div>
            <div><label className={lbl}>Callback number</label>
              <input data-testid="search-phone" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className={inp} /></div>
            <div className="col-span-2"><label className={lbl}>Address / location</label>
              <input data-testid="search-address" value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} className={inp} /></div>
            <div><label className={lbl}>Call type</label>
              <input data-testid="search-calltype" value={f.call_type} onChange={(e) => setF({ ...f, call_type: e.target.value })} placeholder="Label or code" className={inp} /></div>
            <div><label className={lbl}>Assigned unit</label>
              <input data-testid="search-unit" value={f.unit} onChange={(e) => setF({ ...f, unit: e.target.value })} className={inp} /></div>
            <div><label className={lbl}>Status</label>
              <select data-testid="search-status" value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} className={inp}>
                {STATUSES.map((s) => <option key={s} value={s}>{s ? (STATUS_OPT_LABELS[s] || s.toUpperCase()) : "Any status"}</option>)}
              </select></div>
            <div><label className={lbl}>Priority</label>
              <select data-testid="search-priority" value={f.priority} onChange={(e) => setF({ ...f, priority: e.target.value })} className={inp}>
                <option value="">Any</option>
                <option value="0">P0</option><option value="1">P1</option><option value="2">P2</option>
                <option value="3">P3</option><option value="4">P4</option><option value="5">P5</option>
              </select></div>
            <div><label className={lbl}>From (date)</label>
              <input data-testid="search-from" type="date" value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} className={inp} /></div>
            <div><label className={lbl}>To (date)</label>
              <input data-testid="search-to" type="date" value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} className={inp} /></div>
          </div>
          <div className="flex items-center gap-2">
            <button type="submit" data-testid="search-run" disabled={busy}
              className="flex items-center gap-1.5 px-4 py-1.5 text-xs font-head font-bold bg-[#3B82F6] text-white hover:bg-[#2563eb] disabled:opacity-50">
              {busy ? <Loader2 size={13} className="animate-spin" /> : <Search size={13} />} SEARCH
            </button>
            <button type="button" data-testid="search-clear" onClick={() => { setF(EMPTY); setData(null); setError(""); }}
              className="flex items-center gap-1 px-3 py-1.5 text-xs font-head border border-[#2C2D35] text-[#9CA3AF] hover:text-white">
              <Eraser size={12} /> Clear
            </button>
            {data && <span data-testid="search-total" className="ml-auto font-mono text-[11px] text-[#9CA3AF]">{data.total} call(s) · page {data.page}/{data.pages}</span>}
          </div>
        </form>

        {/* results / states */}
        <div className="flex-1 overflow-y-auto">
          {error && (
            <div data-testid="search-error" className="m-4 border-2 border-[#EF4444] bg-[#1c0d0d] px-4 py-3 flex items-center gap-2 font-mono text-xs text-[#EF4444]">
              <AlertTriangle size={14} /> SEARCH ERROR — {error}
            </div>
          )}
          {!error && data && data.results.length === 0 && (
            <div data-testid="search-empty" className="m-8 text-center font-mono text-xs text-[#9CA3AF]">
              No matching calls. Try broadening the date range, removing a filter, or using a partial CAD / case number.
            </div>
          )}
          {!error && data && data.results.length > 0 && (
            <table className="w-full text-left">
              <thead className="font-mono text-[10px] text-[#9CA3AF] uppercase bg-[#0d0e11] sticky top-0 z-10">
                <tr>
                  <TH id="created_at">Date / Time</TH>
                  <TH id="cc_number">CAD #</TH>
                  <th className="px-2 py-2">Case #</th>
                  <th className="px-2 py-2">Location</th>
                  <TH id="call_type">Call Type</TH>
                  <TH id="priority">Pri</TH>
                  <TH id="status">Status</TH>
                  <th className="px-2 py-2">Unit(s)</th>
                  <th className="px-2 py-2">Export</th>
                </tr>
              </thead>
              <tbody>
                {data.results.map((r) => (
                  <tr key={r.id} data-testid={`search-row-${r.cc_number}`} onClick={() => nav(`/incident/${r.id}`)}
                    className="border-b border-[#1E1F24] hover:bg-[#141518] cursor-pointer">
                    <td className="px-2 py-1.5 font-mono text-[10px] text-[#9CA3AF] whitespace-nowrap">{(r.created_at || "").slice(0, 16).replace("T", " ")}</td>
                    <td className="px-2 py-1.5 font-mono text-[12px] font-bold text-[#3B82F6] whitespace-nowrap">{r.cc_number}{r.public_ref ? <span className="text-[#6B7280] font-normal"> · {r.public_ref}</span> : null}</td>
                    <td className="px-2 py-1.5 font-mono text-[11px]">
                      {r.rms_report ? <span className="text-[#10B981] font-bold">{String(r.rms_report)}</span>
                        : <span data-testid={`case-na-${r.cc_number}`} className="text-[#6B7280]">Not assigned</span>}
                    </td>
                    <td className="px-2 py-1.5 text-xs text-[#D1D5DB] max-w-[240px] truncate">{r.location?.address}{r.location?.apt ? ` #${r.location.apt}` : ""}</td>
                    <td className="px-2 py-1.5 text-xs font-head"><span className="flex items-center gap-1.5"><DiscBadge discipline={r.discipline} /> {r.call_type_label}</span></td>
                    <td className="px-2 py-1.5"><PriorityBadge priority={r.priority} /></td>
                    <td className="px-2 py-1.5 font-mono text-[10px] uppercase" style={{ color: r.status === "closed" ? "#6B7280" : "#10B981" }}>{r.status}</td>
                    <td className="px-2 py-1.5 font-mono text-[10px] text-[#9CA3AF] max-w-[140px] truncate">{(r.assigned_units?.length ? r.assigned_units : r.units_history || []).join(", ") || "—"}</td>
                    <td className="px-2 py-1.5">
                      <button data-testid={`search-pdf-${r.cc_number}`} title="Export government-grade Call Event Report PDF"
                        onClick={(e) => { e.stopPropagation(); downloadCallEventPdf(r.id, r.cc_number); }}
                        className="px-2 py-1 text-[10px] font-head font-bold rounded-sm flex items-center gap-1 border border-[#F59E0B] text-[#F59E0B] hover:bg-[#F59E0B] hover:text-black">
                        <FileDown size={11} /> PDF
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {!error && !data && !busy && (
            <div className="m-8 text-center font-mono text-xs text-[#6B7280]">Enter search criteria and press SEARCH — the entire authorized call history is queried.</div>
          )}
        </div>

        {/* pagination */}
        {data && data.pages > 1 && (
          <div className="border-t border-[#2C2D35] bg-[#0d0e11] px-4 py-2 flex items-center gap-3">
            <button data-testid="search-prev" disabled={page <= 1 || busy} onClick={() => run(page - 1)}
              className="flex items-center gap-1 px-2 py-1 text-xs font-head border border-[#2C2D35] text-[#9CA3AF] disabled:opacity-40 hover:text-white"><ChevronLeft size={12} /> Prev</button>
            <span className="font-mono text-[11px] text-[#9CA3AF]">Page {data.page} of {data.pages} · {data.total} total</span>
            <button data-testid="search-next" disabled={page >= data.pages || busy} onClick={() => run(page + 1)}
              className="flex items-center gap-1 px-2 py-1 text-xs font-head border border-[#2C2D35] text-[#9CA3AF] disabled:opacity-40 hover:text-white">Next <ChevronRight size={12} /></button>
          </div>
        )}
      </div>
    </Layout>
  );
}
